import forge from "node-forge";
import { CRYPTO_CONSTANTS } from "./cryptoConstants";
import { cryptoProvider } from "./cryptoProvider";

/**
 * Encrypts a string to Base64, using a public key provided by the back-end.
 * This implementation follows patterns similar to Bouncy Castle for consistency across platforms.
 * @function
 * @name encrypt
 * @kind function
 * @param {string} stringToEncrypt The string to be encrypted.
 * @param {string} publicKeyStr The public key provided by the back-end, as a string.
 * @param {string} algorithm Optional parameter to specify the hash algorithm ('sha256' or 'sha1' or 'pkcs1'). Default is 'sha256'.
 * @returns {string | undefined} The original string encrypted in Base64.
 * @exports
 */
export function encrypt(
  stringToEncrypt: string,
  publicKeyStr: string,
  algorithm:
    | "sha256"
    | "sha1"
    | "pkcs1" = CRYPTO_CONSTANTS.DEFAULT_HASH_ALGORITHM as "sha256"
): string | undefined {
  if (!cryptoProvider.isInitialized()) {
    console.error("Crypto provider not initialized, cannot encrypt data");
    return undefined;
  }

  if (!stringToEncrypt) {
    console.error("The string to be encrypted is not valid.");
    return undefined;
  }

  if (!publicKeyStr) {
    console.error(
      `The public key value (${publicKeyStr}) has too few bytes to parse.`
    );
    return undefined;
  }

  try {
    // Use the crypto provider to convert the public key from base64
    const publicKey = cryptoProvider.publicKeyFromBase64(publicKeyStr);

    // Generate a hash of the public key for comparison
    const modString = publicKey.n.toString(16); // Convert to hex string
    const modHash = cryptoProvider
      .getMessageDigest("sha256")
      .update(modString)
      .digest()
      .toHex();

    // Use the provider to encrypt the data
    const stringEncryptedInB64 = cryptoProvider.rsaEncrypt(
      stringToEncrypt,
      publicKey,
      algorithm
    );

    return stringEncryptedInB64;
  } catch (error) {
    console.error(`Error during encryption with ${algorithm}:`, error);
    console.error("Stack trace:", (error as Error).stack);
    return undefined;
  }
}
